class ConnectionManager {

    constructor(XMPPResource, authProvider, config) {
        this.config = config;
        this.XMPPResource = XMPPResource;
        this.STATE_CONNECTING = 'STATE_CONNECTING';
        this.STATE_CONNECTED = 'STATE_CONNECTED';
        this.STATE_DISCONNECTED = 'STATE_DISCONNECTED';
        this.STATE_GETTING_AUTH_TOKEN = 'STATE_GETTING_AUTH_TOKEN';
        this.listeners = [];
        this.reconnectAllowed = true;
        this.reconnecting = false;
        this.reconnectAttempt = 0;
        this.reconnectHandle = null;
        this.reconnected = false;
        this.isPinging = false;
        this.failedPings = 0;
        this.showOfflineNotificationAtFailedPing = 2;
        this.pingIntervalSeconds = 30;
        this._state = this.STATE_DISCONNECTED;
        this.host = 'wss://' + config["chat_host"] + '/';
        this.connection = new Strophe.Connection(this.host, {protocol: 'wss'});
        Strophe._handleError = e => {
            Errors.extractStackAndSend(e);
            return true;
        };
        this.gotInput = false;
        this.gotOutput = false;
        this.connection.rawInput = () => {
            if (this.gotOutput) {
                this.initPing();
            } else {
                this.gotInput = true;
            }
        };
        this.connection.rawOutput = () => {
            if (this.gotInput) {
                this.initPing();
            }
            this.gotOutput = true;
        };
        this.handlers = [];
        this.authProvider = authProvider;
        this.connect();
    }

    connect() {
        if (this._state === this.STATE_DISCONNECTED) {
            this._getAuthToken();
        }
    }

    disconnect() {
        if (this.connection) {
            this.connection.flush();
            this.connection.disconnect();
        }
    }

    initPing() {
        this.gotInput = false;
        this.gotOutput = false;
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
        }
        this.pingInterval = setInterval(() => {
            this.ping();
        }, this.pingIntervalSeconds * 1000);
    }

    ping() {
        if (this.isPinging) {
            return;
        }
        this.isPinging = true;
        if (this._state === this.STATE_DISCONNECTED) {
            this._reConnect();
            if (++this.failedPings === this.showOfflineNotificationAtFailedPing) {
                app.notifications.addSystemNotification(0, "danger", app.config.translate.network_disconnected);
                app.notifications.revealSystemNotification(0);
            }
        } else if (this._state === this.STATE_CONNECTED) {
            if (this.gotInput && this.gotOutput) {
                this.gotInput = false;
                this.gotOutput = false;
                this.isPinging = false;
                return;
            }
            this.connection.sendIQ($iq({type: 'get', to: app.config.user.u + "@" + app.config.domain, id: this.connection.getUniqueId('ping')}).c(
                'ping', {xmlns: "urn:xmpp:ping"}), (e) => {
                if (this.failedPings) {
                    app.notifications.removeSystemNotification(0);
                    this.failedPings = 0;
                }
            }, () => {
                if (this.gotInput && this.gotOutput) {
                    this.gotInput = false;
                    this.gotOutput = false;
                    this.isPinging = false;
                    return;
                }
                this.disconnect();
                this._reConnect();
            }, (this.pingIntervalSeconds * 1000) * 0.9);
        }
        this.isPinging = false;
    }

    send(stanza) {
        if (this._state === this.STATE_CONNECTED) {
            this.connection.send(stanza);
        }
    }

    sendPresence(location) {
        let p = $pres({
            to: this.getRoomJID(location) + "/" + this.config.user.u
        }).c("user").t(JSON.stringify(this.config.user)).tree();
        this.send(p);
    }

    getRoomJID(str) {
        return str + '@conference.' + this.config.domain;
    }

    addHandler(handlerFunction, ns, name, type, id, from, options) {
        const handler = {
            handlerFunction: handlerFunction,
            ns: ns,
            name: name,
            type: type,
            id: id,
            from: from,
            options: options,
            handlerRef: null
        };
        this.handlers.push(handler);

        if (this.isConnected()) {
            this._addHandlerToStrophe(handler);
        }

        return handler;
    }

    deleteHandler(handler) {
        this.connection.deleteHandler(handler.handlerRef);
    }

    addListener(listener) {
        this.listeners.push(listener);
    }

    isConnected() {
        return this._state === this.STATE_CONNECTED;
    }

    getUniqueId() {
        return this.connection.getUniqueId();
    }

    getBareJID() {
        return this.XMPPResource.bareJID();
    }

    getFullJID() {
        return this.XMPPResource.fullJID();
    }

    closeConnection() {
        this.reconnectAllowed = false;
        this.disconnect();
    }

    _addHandlerToStrophe(handler) {
        handler.handlerRef = this.connection.addHandler(
            handler.handlerFunction,
            handler.ns,
            handler.name,
            handler.type,
            handler.id,
            handler.from,
            handler.options
        );
        return true;
    }

    _getAuthToken() {
        this._state = this.STATE_GETTING_AUTH_TOKEN;
        this.authProvider.getToken(this._onAuthToken.bind(this));
        return true;
    }

    _onAuthToken(token) {
        this._state = this.STATE_CONNECTING;
        this.connection.reset();
        this.connection.connect(this.getFullJID(), token, this._onConnectionStatusChanged.bind(this));
        return true;
    }

    _reConnect() {
        if (this.reconnectAllowed === false) {
            return true;
        }
        this.reconnecting = true;
        this.connect();
        return true;
    }

    _resetReconnect() {
        if (this.reconnecting === true) {
            clearTimeout(this.reconnectHandle);
            this.reconnectHandle = null;
            this.reconnecting = false;
            this.reconnectAttempt = 0;
            this.reconnected = true;
            setTimeout(() => this.reconnected = false, 3000);
        }
        return true;
    }

    _onConnectionStatusChanged(status, error) {
        this.initPing();
        switch (status) {
            case Strophe.Status.CONNECTING:
                this._onConnecting();
                break;
            case Strophe.Status.CONNFAIL:
                this._onConnectionFailed();
                break;
            case Strophe.Status.AUTHFAIL:
                this._onAuthFail();
                break;
            case Strophe.Status.DISCONNECTING:
                this._onDisconnecting();
                break;
            case Strophe.Status.DISCONNECTED:
                this._onDisconnected();
                break;
            case Strophe.Status.CONNECTED:
                this._onConnected();
                break;
            case Strophe.Status.ERROR:
                this.closeConnection();
                break;
            default:
                Logger.debug("unhandle case in _onConnectionStatusChanged", "connection_manager.js:170");
        }
        if (typeof error === "string") {
            Logger.error(error);
        }
        return true;
    }

    _onConnected() {
        if (this.failedPings) {
            app.notifications.removeSystemNotification(0);
            this.failedPings = 0;
        }
        if (this.reconnecting === true) {
            this._resetReconnect();
        }
        this._state = this.STATE_CONNECTED;
        this.handlers.forEach(function (handler) {
            this._addHandlerToStrophe(handler);
        }.bind(this));
        this.listeners.forEach(function (listener) {
            listener.onConnected();
        });

        return true;
    }

    _onDisconnected() {
        this._state = this.STATE_DISCONNECTED;
        this.listeners.forEach(function (listener) {
            listener.onDisconnected();
        });
        return true;
    }

    _onConnecting() {
        return true;
    }

    _onConnectionFailed() {
        return true;
    }

    _onAuthFail() {
        return true;
    }

    _onDisconnecting() {
        this._state = this.STATE_DISCONNECTED;
        return true;
    }

    _onError(e) {
        Errors.extractStackAndSend(e);
        return true;
    }
}